import { Children, CSSProperties, useCallback, useMemo } from "react";
import Select, { components, StylesConfig, ValueContainerProps } from "react-select";

import { hasValue, styles } from "@repo/helpers";

import { InputComponent } from "../../basic/input";
import { DdlValue, WebMarkupProps } from "../types";
import style from "./style.module.scss";

export default function DropDownMarkup(props: WebMarkupProps) {
	const {
		valueProp = "value",
		textProp = "label",
		values,
		loaded = true,
		clearable,
		highlightedValues = new Set<any>(),
		optionStyle,
		maxSelectedDisplay,
		fullLength,
		...selectProps
	} = props;
	const { isMulti, ...inputProps } = selectProps;
	const { id, name, value, disabled, className } = inputProps;

	const highlightStyle = useCallback(
		(data: DdlValue) =>
			({
				fontWeight: highlightedValues.has(data.value) ? "bold" : "normal",
				...(optionStyle && optionStyle(data)),
			}) as CSSProperties,
		[highlightedValues, optionStyle],
	);

	const localStyle = useMemo(
		() =>
			({
				container: (base: CSSProperties, state: any) => ({
					...base,
					zIndex: "auto !important",
				}),
				control: (base: CSSProperties, state: any) => ({
					...base,
					// match with the menu
					borderRadius: !loaded ? "4px 0 0 4px" : 4,
					borderRight: !loaded ? 0 : base.borderRight,
				}),
				menu: (base: CSSProperties, state: any) => ({
					...base,
					zIndex: 999,
				}),
				option: (base: CSSProperties, { data }) => ({
					...base,
					...highlightStyle(data),
				}),
			}) as StylesConfig<DdlValue>,
		[highlightStyle, loaded],
	);
	const fullStyle = useMemo(() => ({ ...localStyle }), [localStyle]);

	const vals = useMemo(() => {
		if (valueProp !== undefined && textProp !== undefined && values && values.map) {
			return values.map((val: any) => {
				return {
					value: val[valueProp],
					label: val[textProp],
				};
			});
		}
		return values;
	}, [values, valueProp, textProp]);

	const minimizingValues = isMulti && hasValue(maxSelectedDisplay) && (value as []).length >= maxSelectedDisplay;

	const hideSelectedOption = props.hideSelected ?? isMulti;
	const hideSelected = hideSelectedOption && !minimizingValues;

	//NOTE: maybe turn of show selected if minimizing display??? will need to figure out how it works
	const fullClassName = styles(style.selectControl, fullLength && style.fullLength, className);
	return (
		<InputComponent {...inputProps}>
			<Select
				{...selectProps}
				styles={fullStyle}
				className={fullClassName}
				isDisabled={disabled || !loaded}
				name={name || id}
				components={{ ValueContainer }}
				options={vals}
				isLoading={!loaded}
				value={value || null}
				isClearable={clearable}
				hideSelectedOptions={hideSelected}
				//@ts-ignore
				minimizingValues={minimizingValues}
			/>
		</InputComponent>
	);
}

/**
 * This needs to be defined outside of normal markup
 * Needs to explicitly pass in "minimizingValues" as we need access to it, and this is the only way it gets picked up
 * ts-ignore is so it compiles in spite of this
 * @param args
 * @returns
 */
function ValueContainer(args: ValueContainerProps<DdlValue>) {
	const { children, selectProps, getValue } = args;
	//@ts-ignore
	const { minimizingValues } = selectProps;
	const value = getValue();
	const allChildren = Children.toArray(children) as any[];
	let selectedValues = allChildren.slice(0, value.length) as any;
	//Will have an Input component, and may have a Placeholder
	//Always comes after the values, if any
	const otherChildren = allChildren.slice(value.length);
	if (minimizingValues) {
		const plural = value.length === 1 ? "" : "s";
		selectedValues = `${value.length} item${plural}...`;
	}
	return (
		<components.ValueContainer {...args}>
			{selectedValues}
			{otherChildren}
		</components.ValueContainer>
	);
}
